import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
} from '@material-ui/core';
import axios from 'axios';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

import { withStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { change, Field, initialize, reduxForm } from 'redux-form';

import Close from '@material-ui/icons/Close';
import classNames from 'classnames';
import {
    apiCatchBlockFunction,
    filterFavoriteAccountOnType,
    getAllowedDefaultAccountTypes,
} from '../../constants/CommonUtil';
import { required } from '../../constants/FormValidations';
import API from '../../constants/api';
import { PAYMENT_METHODS, PAYMENT_METHODS_MAP } from '../../constants/constants';
import { getStringFromObject } from '../../constants/lodashUtils';
import { isObjectValidAndNotEmpty } from '../../constants/nullCheckUtils';
import { successMessage } from '../../redux/modules/message/message-actions';
import ActionButton from '../ActionButton/ActionButton';
import dialogComponentStyles from '../DialogComponent/DialogComponentStyles';
import DateInput from '../FormFieldComponents/DateInput/DateInput';
import ReduxFormRadio from '../FormFieldComponents/ReduxFormRadio/ReduxFormRadio';
import NumberTextField from '../NumberTextField';
import OutlinedTextField from '../OutlinedTextField';
import ReduxFormReactSelectMaterial from '../ReduxFormReactSelectMaterial';
import { fetchAccountFavorites } from '../../redux/modules/favoriteAccounts/favoritesForDoctor-actions';
import { showSpinner, hideSpinner } from '../../redux/modules/spinner/spinner';
import {
    BANK_ENTRY_FORM,
    RADIO_CHOICES,
    notSameAccount,
    positiveNumber,
    dtoToFormValues,
    buildPayload,
    getAccountingMessage,
} from './AddBankEntryDialogUtil';

class AddBankEntryDialog extends Component {
    constructor(props) {
        super(props);
        this.state = {
            subCompany: null,
        };
    }

    componentDidMount() {
        const {
            dispatch,
            uuid,
        } = this.props;

        this.props.dispatch(fetchAccountFavorites());
        if (uuid) {
            axios.get(`${API.BANK_ENTRIES.GET}${uuid}`)
                .then((response) => {
                    dispatch(initialize(BANK_ENTRY_FORM, dtoToFormValues(response.data)));
                    this.setState({ subCompany: getStringFromObject('data.subCompany', response) });
                })
                .catch(err => apiCatchBlockFunction(err, dispatch));
        } else {
            dispatch(initialize(BANK_ENTRY_FORM, {
                type: 'payment',
                entryDate: new Date().getTime(),
            }));
        }
    }

    handleBankAccountSelect = (newAccount) => {
        const { dispatch } = this.props;

        if (isObjectValidAndNotEmpty(newAccount)) {
            this.setState({ subCompany: newAccount.subCompany });
        } else {
            this.setState({ subCompany: null });
        }

        dispatch(change(BANK_ENTRY_FORM, 'againstAccount', null));
    }

    handleSaveDraft = (formValues) => {
        this.submitBankEntry(formValues, false);
    }

    handleSaveAndConfirm = (formValues) => {
        this.submitBankEntry(formValues, true);
    }

    submitBankEntry = async (formValues, confirm) => {
        const { dispatch, handleClose, uuid } = this.props;
        const { subCompany } = this.state;

        const payload = buildPayload(formValues, subCompany, confirm, uuid);

        dispatch(showSpinner());
        try {
            await axios.post(API.BANK_ENTRIES.ADD_OR_UPDATE, payload);

            const successMsg = confirm
                ? 'Bank Entry confirmed and Journal Entry created successfully!'
                : 'Bank Entry saved successfully!';
            dispatch(successMessage(successMsg));
            handleClose();
        } catch (error) {
            apiCatchBlockFunction(error, dispatch);
        } finally {
            dispatch(hideSpinner());
        }
    }

    render() {
        const {
            classes,
            handleClose,
            handleSubmit,
            formValues,
            allowedAccountTypes,
            favorites,
        } = this.props;
        const { subCompany } = this.state;

        const paymentMethod = getStringFromObject('paymentMethod', formValues);
        const isCheque = paymentMethod && paymentMethod.value === PAYMENT_METHODS_MAP.CHEQUE.value;
        const isBank = paymentMethod && paymentMethod.value === PAYMENT_METHODS_MAP.BANKTRANSFER.value;

        const type = getStringFromObject('type', formValues) || 'payment';
        const bankAccountName = getStringFromObject('bankAccount.value', formValues);
        const againstAccountName = getStringFromObject('againstAccount.value', formValues);

        const accountingMessage = getAccountingMessage(type, bankAccountName, againstAccountName);

        return (
            <Dialog
                open
                maxWidth="md"
                scroll="body"
                aria-labelledby="form-dialog-title"
                className={classes.dialog}
                classes={{ paper: classes.paper }}
                PaperProps={{
                    style: {
                        alignSelf: 'flex-start',
                        marginTop: '10vh',
                    },
                }}
            >
                <DialogTitle disableTypography className={classes.title}>
                    <Grid container justify="space-between">
                        <div className={classes.header}>Add Bank Entry</div>
                        <Close
                            className={classNames(classes.closeIcon, 'cursor-pointer')}
                            onClick={handleClose}
                        />
                    </Grid>
                </DialogTitle>

                <DialogContent className={classes.dialogContent}>
                    <Grid container spacing={24} className="mt-1">
                        <Grid item lg={4} md={4} sm={12}>
                            <Field
                                name="bankAccount"
                                component={ReduxFormReactSelectMaterial}
                                label="Bank Account"
                                required
                                validate={[required]}
                                autocomplete
                                defaultOptions
                                selectProps={{ textFieldProps: { variant: 'outlined' } }}
                                dataSourceApi={`${API.SEARCH.ACCOUNTS}?type=${
                                    getAllowedDefaultAccountTypes('bankEntryBankAccountTypes', allowedAccountTypes)
                                }&searchString=`}
                                dataSourceConfig={{ text: 'value', value: 'id' }}
                                onSelectHandlers={['handleBankAccountSelect']}
                                actionHandlers={{ handleBankAccountSelect: this.handleBankAccountSelect }}
                                fixedOptionSet={filterFavoriteAccountOnType(favorites, `${API.SEARCH.ACCOUNTS}?type=${getAllowedDefaultAccountTypes('bankEntryBankAccountTypes', allowedAccountTypes)}&searchString=`)}
                            />
                        </Grid>
                        <Grid item lg={4} md={4} sm={12}>
                            <Field
                                key={subCompany || ''}
                                isDisabled={!subCompany}
                                name="againstAccount"
                                component={ReduxFormReactSelectMaterial}
                                label="Against Account"
                                required
                                validate={[required, notSameAccount]}
                                autocomplete
                                defaultOptions
                                selectProps={{ textFieldProps: { variant: 'outlined' } }}
                                dataSourceApi={`${API.SEARCH.ACCOUNTS}?type=${
                                    getAllowedDefaultAccountTypes('bankEntryAgainstAccountTypes', allowedAccountTypes)
                                }&subCompany=${subCompany || ''}&searchString=`}
                                dataSourceConfig={{ text: 'value', value: 'id' }}
                                fixedOptionSet={filterFavoriteAccountOnType(favorites, `${API.SEARCH.ACCOUNTS}?type=${getAllowedDefaultAccountTypes('bankEntryAgainstAccountTypes', allowedAccountTypes)}&subCompany=${subCompany || ''}&searchString=`)}
                            />
                        </Grid>
                        <Grid item lg={4} md={4} sm={12}>
                            <Field
                                name="entryDate"
                                component={DateInput}
                                label="Entry Date"
                                required
                                validate={[required]}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item lg={4} md={4} sm={12}>
                            <Field
                                name="paymentMethod"
                                component={ReduxFormReactSelectMaterial}
                                label="Payment Method"
                                required
                                validate={[required]}
                                options={PAYMENT_METHODS}
                                dataSourceConfig={{ text: 'label', value: 'value' }}
                                selectProps={{ textFieldProps: { variant: 'outlined' } }}
                            />
                        </Grid>
                        <Grid item lg={4} md={4} sm={12}>
                            <Field
                                name="amount"
                                label="Amount"
                                type="number"
                                required
                                validate={[required, positiveNumber]}
                                component={NumberTextField}
                            />
                        </Grid>
                        <Grid item lg={4} md={4} sm={12}>
                            <Field
                                name="type"
                                label=""
                                choices={RADIO_CHOICES}
                                dataSourceConfig={{ text: 'value', value: 'key' }}
                                component={ReduxFormRadio}
                            />
                        </Grid>

                        {isObjectValidAndNotEmpty(paymentMethod) && (isCheque || isBank) && (
                            <React.Fragment>
                                <Grid item lg={4} md={4} sm={12}>
                                    <Field
                                        name="bankName"
                                        label="Bank Name"
                                        placeholder="Enter Bank Name"
                                        component={OutlinedTextField}
                                    />
                                </Grid>
                                <Grid item lg={4} md={4} sm={12}>
                                    <Field
                                        name="refNumber"
                                        label={isCheque ? 'Cheque Number' : 'Reference Number'}
                                        placeholder={isCheque ? 'Enter Cheque No' : 'Enter Reference No'}
                                        required
                                        validate={[required]}
                                        component={OutlinedTextField}
                                    />
                                </Grid>
                                <Grid item lg={4} md={4} sm={12}>
                                    <Field
                                        name="date"
                                        component={DateInput}
                                        label={isCheque ? 'Cheque Date' : 'Date'}
                                        InputLabelProps={{ shrink: true }}
                                        required
                                        validate={required}
                                    />
                                </Grid>
                                <Grid item lg={12} md={12} sm={12}>
                                    <Field
                                        name="remarks"
                                        label="Remarks"
                                        component={OutlinedTextField}
                                    />
                                </Grid>
                            </React.Fragment>
                        )}

                        <Grid item lg={8} md={8} sm={8}>
                            <Field
                                name="memo"
                                component={OutlinedTextField}
                                label="Memo"
                                multiline
                                rows={2}
                                rowsMax
                                fullWidth
                                required
                                validate={[required]}
                            />
                        </Grid>
                        <Grid item lg={4} md={4} sm={4}>
                            <Field
                                name="journalId"
                                component={ReduxFormReactSelectMaterial}
                                dataSourceConfig={{
                                    text: 'name',
                                    value: 'id',
                                }}
                                style={{ padding: '6px' }}
                                autocomplete
                                required
                                validate={[required]}
                                label="Select Journal"
                                placeholder="Select Journal"
                                selectProps={{
                                    textFieldProps: {
                                        InputProps: {
                                            classes: {
                                                multiline: classes.multiline,
                                            },
                                            disableUnderline: true,
                                        },
                                    },
                                }}
                                dataSourceApi={API.SEARCH.ACCOUNT_JOURNAL_EXCEPT_OPENING_JOURNAL}
                            />
                        </Grid>
                    </Grid>
                    {accountingMessage && (
                        <div style={{
                            backgroundColor: '#e3f2fd',
                            color: '#4a8bec',
                            fontSize: '1rem',
                            padding: '10px 14px',
                            borderRadius: '4px',
                            margin: '10px 0px',
                            fontWeight: 500,
                        }}
                        >
                            {accountingMessage}
                        </div>
                    )}
                </DialogContent>

                <DialogActions style={{ padding: '16px' }}>
                    <Grid container justify="flex-end">
                        <ActionButton
                            primary={false}
                            onClick={handleClose}
                            disableRipple
                        >
                            Cancel
                        </ActionButton>

                        <ActionButton
                            className="ml-1"
                            onClick={handleSubmit(this.handleSaveDraft)}
                            disableRipple
                            primary={false}
                        >
                            Save
                        </ActionButton>

                        <ActionButton
                            className="ml-1"
                            onClick={handleSubmit(this.handleSaveAndConfirm)}
                            disableRipple
                        >
                            Confirm
                        </ActionButton>
                    </Grid>
                </DialogActions>
            </Dialog>
        );
    }
}

AddBankEntryDialog.propTypes = {
    classes: PropTypes.object.isRequired,
    handleClose: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
    formValues: PropTypes.object,
    allowedAccountTypes: PropTypes.object,
    uuid: PropTypes.string,
    favorites: PropTypes.array,
};

AddBankEntryDialog.defaultProps = {
    formValues: {},
    allowedAccountTypes: {},
    uuid: null,
    favorites: [],
};

const mapStateToProps = state => ({
    formValues: state.form[BANK_ENTRY_FORM] ? state.form[BANK_ENTRY_FORM].values : {},
    allowedAccountTypes: getStringFromObject('appConfiguration.allowedAccountTypes', state),
    favorites: getStringFromObject('favoriteForDoctor', state),
});

export default connect(mapStateToProps)(reduxForm({
    form: BANK_ENTRY_FORM,
})(withStyles(dialogComponentStyles)(AddBankEntryDialog)));
